/**
 * @file command.tsx
 */

"use client";

import * as React from "react";
import { Command as CommandPrimitive } from "cmdk";
import { AlertCircle, Loader2, SearchIcon } from "lucide-react";

import { cn } from "visuais/fmt";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from ".";

/**
 * @returns {JSX.Element} Componente de comando.
 * @example
 * ```tsx
 * <Command
 *   className="bg-azul"
 * >
 *   <CommandInput placeholder="Pesquisar..." />
 *   <CommandList>
 *     <CommandEmpty>Nenhum resultado encontrado</CommandEmpty>
 *     <CommandGroup>
 *       <CommandItem>Item 1</CommandItem>
 *     </CommandGroup>
 *   </CommandList>
 * </Command>
 * ```
 */
export function Command({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive>) {
  return (
    <CommandPrimitive
      data-slot="command"
      className={cn(
        "bg-popover/40 backdrop-blur-2xl text-popover-foreground flex h-full w-full flex-col overflow-hidden rounded-xl",
        className
      )}
      {...props}
    />
  );
}

/**
 * @returns {JSX.Element} Diálogo de comando.
 * @example
 * ```tsx
 * <CommandDialog
 *   title="Paleta de Comandos"
 *   description="Pesquise um comando para executar..."
 * >
 *   <CommandInput placeholder="Pesquisar..." />
 * </CommandDialog>
 * ```
 */
export function CommandDialog({
  title = "Paleta de Comandos",
  description = "Pesquise um comando para executar...",
  children,
  className,
  showCloseButton = true,
  ...props
}: React.ComponentProps<typeof Dialog> & {
  title?: string;
  description?: string;
  className?: string;
  showCloseButton?: boolean;
}) {
  return (
    <Dialog {...props}>
      <DialogHeader className="sr-only">
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>{description}</DialogDescription>
      </DialogHeader>
      <DialogContent
        className={cn(
          "overflow-hidden p-0 border-4 border-popover rounded-xl",
          className
        )}
        showCloseButton={showCloseButton}
      >
        <Command className="[&_[cmdk-group-heading]]:text-muted-foreground **:data-[slot=command-input-wrapper]:h-12 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group]]:px-2 [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5">
          {children}
        </Command>
      </DialogContent>
    </Dialog>
  );
}

/**
 * @returns {JSX.Element} Input de comando.
 * @example
 * ```tsx
 * <CommandInput
 *   placeholder="Pesquisar..."
 *   className="bg-cinza"
 * />
 * ```
 */
export function CommandInput({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Input>) {
  return (
    <div
      data-slot="command-input-wrapper"
      className="flex h-9 items-center gap-2 border-b px-3"
    >
      <SearchIcon className="size-4 shrink-0 opacity-50" />
      <CommandPrimitive.Input
        data-slot="command-input"
        className={cn(
          "placeholder:text-muted-foreground flex h-10 w-full rounded-sm py-3 text-sm outline-hidden disabled:cursor-not-allowed disabled:opacity-50",
          className
        )}
        {...props}
      />
    </div>
  );
}

/**
 * @returns {JSX.Element} Lista de comando.
 * @example
 * ```tsx
 * <CommandList>
 *   <CommandEmpty>Nenhum resultado encontrado</CommandEmpty>
 *   <CommandGroup>
 *     <CommandItem>Item 1</CommandItem>
 *   </CommandGroup>
 * </CommandList>
 * ```
 */
export function CommandList({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.List>) {
  return (
    <CommandPrimitive.List
      data-slot="command-list"
      className={cn("overflow-x-hidden overflow-y-auto", className)}
      {...props}
    />
  );
}

/**
 * @returns {JSX.Element} Mensagem vazia do comando.
 * @example
 * ```tsx
 * <CommandEmpty>Nenhum resultado encontrado</CommandEmpty>
 * ```
 */
export function CommandEmpty({
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Empty>) {
  return (
    <CommandPrimitive.Empty
      data-slot="command-empty"
      className="py-6 text-center text-sm"
      {...props}
    />
  );
}

/**
 * @returns {JSX.Element} Grupo de comando.
 * @example
 * ```tsx
 * <CommandGroup>
 *   <CommandItem>Item 1</CommandItem>
 * </CommandGroup>
 * ```
 */
export function CommandGroup({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Group>) {
  return (
    <CommandPrimitive.Group
      data-slot="command-group"
      className={cn(
        "text-foreground [&_[cmdk-group-heading]]:text-muted-foreground overflow-hidden p-2 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium",
        className
      )}
      {...props}
    />
  );
}

/**
 * @returns {JSX.Element} Separador de comando.
 * @example
 * ```tsx
 * <CommandSeparator />
 * ```
 */
export function CommandSeparator({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Separator>) {
  return (
    <CommandPrimitive.Separator
      data-slot="command-separator"
      className={cn("bg-border -mx-1 h-px", className)}
      {...props}
    />
  );
}

/**
 * @returns {JSX.Element} Item de comando.
 * @example
 * ```tsx
 * <CommandItem>Item 1</CommandItem>
 * ```
 */
export function CommandItem({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Item>) {
  const commandItemClass =
    // Layout & posicionamento
    "relative flex items-start gap-2 cursor-default select-none outline-hidden" +
    " px-2 py-2 rounded-lg text-sm transition-colors shadow" +
    // Aparência padrão
    " bg-transparent border" +
    // Ícones
    " [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4" +
    // Estados
    " data-[selected=true]:border-primary" +
    " dark:data-[selected=true]:bg-input/50" +
    " data-[selected=true]:text-accent-foreground" +
    " data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50";

  return (
    <CommandPrimitive.Item
      data-slot="command-item"
      className={cn(commandItemClass, className)}
      {...props}
    />
  );
}

/**
 * @returns {JSX.Element} Atalho de comando.
 * @example
 * ```tsx
 * <CommandItem>
 *   Item 1 <CommandShortcut>Ctrl+C</CommandShortcut>
 * </CommandItem>
 * ```
 */
export function CommandShortcut({
  className,
  ...props
}: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="command-shortcut"
      className={cn(
        "text-muted-foreground ml-auto text-xs tracking-widest",
        className
      )}
      {...props}
    />
  );
}
/**
 * @returns {JSX.Element} Estado de carregamento para CommandList.
 * @example
 * ```tsx
 * <CommandList>
 *   <CommandLoading>Buscando pedidos…</CommandLoading>
 * </CommandList>
 * ```
 */
export function CommandLoading({
  className,
  children = "Carregando...",
  ...props
}: React.ComponentProps<"div"> & {
  children?: React.ReactNode;
}) {
  return (
    <div
      data-slot="command-loading"
      className={cn(
        "flex flex-col items-center justify-center gap-3 py-10 text-sm text-muted-foreground",
        className
      )}
      {...props}
    >
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
      <span>{children}</span>
    </div>
  );
}

/**
 * @returns {JSX.Element} Estado de erro para CommandList.
 * @example
 * ```tsx
 * <CommandList>
 *   <CommandError>Erro ao buscar dados</CommandError>
 * </CommandList>
 * ```
 */
export function CommandError({
  className,
  children = "Erro",
  ...props
}: React.ComponentProps<"div"> & {
  children?: React.ReactNode;
}) {
  return (
    <div
      data-slot="command-error"
      className={cn(
        "flex flex-col items-center justify-center gap-2 py-10 text-sm text-muted-foreground",
        className
      )}
      {...props}
    >
      <AlertCircle className="h-8 w-8 text-destructive" />
      <div className="font-semibold text-destructive text-center">
        {children}
      </div>
    </div>
  );
}

/**
 * @returns {JSX.Element} Estado vazio para CommandList.
 * @example
 * ```tsx
 * <CommandList>
 *   <CommandNotFound>Nenhum pedido encontrado</CommandNotFound>
 * </CommandList>
 * ```
 */
export function CommandNotFound({
  className,
  children = "Nenhum resultado encontrado",
  ...props
}: React.ComponentProps<"div"> & {
  children?: React.ReactNode;
}) {
  return (
    <div
      data-slot="command-not-found"
      className={cn(
        "flex flex-col items-center justify-center gap-3 py-10 text-center text-sm text-muted-foreground",
        className
      )}
      {...props}
    >
      <SearchIcon className="h-8 w-8 text-muted-foreground" />
      <span>{children}</span>
    </div>
  );
}
